// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { SettingsContext } from './settings-context';

export const SettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const getFromStorage = <T,>(key: string, defaultValue: T, parseJson = true): T => {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const value = localStorage.getItem(key);
      if (value === null) return defaultValue;
      return parseJson ? JSON.parse(value) : (value as unknown as T);
    } catch {
      return defaultValue;
    }
  };

  const [notifications, setNotifications] = useState<boolean>(
    getFromStorage('notifications', true)
  );
  const [speakingNotifications, setSpeakingNotifications] = useState<boolean>(
    getFromStorage('speakingNotifications', true)
  );

  const [captureSystemAudio, setCaptureSystemAudio] = useState<boolean>(
    getFromStorage('captureSystemAudio', false)
  );

  const [autoConnectVoice, setAutoConnectVoice] = useState<boolean>(
    getFromStorage('autoConnectVoice', true)
  );

  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);

  const [selectedAudioOutputDeviceId, setSelectedAudioOutputDeviceId] = useState<string>(
    getFromStorage('audioOutputDeviceId', '', false)
  );

  const [audioOutputError, setAudioOutputError] = useState<string>('');

  const [sfxVolume, setSfxVolume] = useState<number>(
    Math.min(1.0, Math.max(0.0, getFromStorage('sfxVolume', 0.1)))
  );

  const [autoGainControl, setAutoGainControl] = useState<boolean>(
    getFromStorage('autoGainControl', true)
  );

  const [noiseSuppression, setNoiseSuppression] = useState<boolean>(getFromStorage('CENC', true));

  const [echoCancellation, setEchoCancellation] = useState<boolean>(
    getFromStorage('echoCancellation', true)
  );

  const [mutedServers, setMutedServers] = useState<Record<string, boolean>>(
    getFromStorage('mutedServers', {})
  );

  const [userStartPlayingNotifications, setUserStartPlayingNotifications] = useState<boolean>(
    getFromStorage('userStartPlayingNotifications', true)
  );

  const [spatialAudioEnabled, setSpatialAudioEnabled] = useState<boolean>(false);

  const [selectedAudioInputDeviceId, setSelectedAudioInputDeviceId] = useState<string>(
    getFromStorage('audioInputDeviceId', 'default', false)
  );

  const [receiveScreenShare, setReceiveScreenShare] = useState<boolean>(
    getFromStorage('receiveScreenShare', true)
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const migrateSettings = [
        {
          cookieKey: 'notifications',
          storageKey: 'notifications',
          setter: setNotifications,
          isJson: true,
        },
        {
          cookieKey: 'autoconnectVoice',
          storageKey: 'autoConnectVoice',
          setter: setAutoConnectVoice,
          isJson: true,
        },
        {
          cookieKey: 'audioOutputDeviceId',
          storageKey: 'audioOutputDeviceId',
          setter: setSelectedAudioOutputDeviceId,
          isJson: false,
        },
        {
          cookieKey: 'audioInputDeviceId',
          storageKey: 'audioInputDeviceId',
          setter: setSelectedAudioInputDeviceId,
          isJson: false,
        },
      ];

      migrateSettings.forEach(({ cookieKey, storageKey, setter, isJson }) => {
        const cookieValue = Cookies.get(cookieKey);
        if (cookieValue && !localStorage.getItem(storageKey)) {
          const value = isJson ? JSON.parse(cookieValue) : cookieValue;
          localStorage.setItem(storageKey, isJson ? JSON.stringify(value) : value);
          Cookies.remove(cookieKey);
          setter(value);
        }
      });

      const savedNotifications = localStorage.getItem('notifications');
      setNotifications(savedNotifications ? JSON.parse(savedNotifications) : true);

      // const savedCapture = localStorage.getItem('captureSystemAudio');
      // setCaptureSystemAudio(savedCapture ? JSON.parse(savedCapture) : false);
      const savedCapture = localStorage.getItem('captureSystemAudio');
      if (savedCapture !== null) {
        setCaptureSystemAudio(JSON.parse(savedCapture));
      } else {
        localStorage.setItem('captureSystemAudio', JSON.stringify(false));
        setCaptureSystemAudio(false);
      }

      const savedAutoConnect = localStorage.getItem('autoConnectVoice');
      setAutoConnectVoice(savedAutoConnect ? JSON.parse(savedAutoConnect) : true);

      const savedDeviceId = localStorage.getItem('audioOutputDeviceId');
      if (savedDeviceId) {
        setSelectedAudioOutputDeviceId(savedDeviceId);
      }

      const savedSFXVolume = localStorage.getItem('sfxVolume');
      // Ensure volume is between 0.0 and 1.0
      const initialVolume = savedSFXVolume
        ? Math.min(1.0, Math.max(0.0, parseFloat(savedSFXVolume)))
        : 0.1;
      setSfxVolume(initialVolume);

      // --- autoGainControl migration ---
      const savedGain = localStorage.getItem('autoGainControl');
      if (savedGain === null || savedGain === 'false') {
        localStorage.setItem('autoGainControl', JSON.stringify(true)); // один раз принудительно включаем
        setAutoGainControl(true);
      } else {
        setAutoGainControl(JSON.parse(savedGain));
      }

      const savedNoise = localStorage.getItem('CENC');
      setNoiseSuppression(savedNoise ? JSON.parse(savedNoise) : true);

      const savedEcho = localStorage.getItem('echoCancellation');
      setEchoCancellation(savedEcho ? JSON.parse(savedEcho) : true);

      const savedAudioInputDevice = localStorage.getItem('audioInputDeviceId');
      if (savedAudioInputDevice) {
        setSelectedAudioInputDeviceId(savedAudioInputDevice);
      } else {
        // Migrate from cookies if not in localStorage
        const cookieAudioInputDevice = Cookies.get('audioInputDeviceId');
        if (cookieAudioInputDevice) {
          localStorage.setItem('audioInputDeviceId', cookieAudioInputDevice);
          setSelectedAudioInputDeviceId(cookieAudioInputDevice);
        }
      }

      const savedReceiveScreenShare = localStorage.getItem('receiveScreenShare');
      if (savedReceiveScreenShare !== null) {
        setReceiveScreenShare(JSON.parse(savedReceiveScreenShare));
      } else {
        localStorage.setItem('receiveScreenShare', JSON.stringify(true));
      }
    }
  }, []);

  const updateStorage = <T,>(key: string, value: T, parseJson = true) => {
    localStorage.setItem(key, parseJson ? JSON.stringify(value) : (value as unknown as string));
  };

  useEffect(() => {
    if (sfxVolume === 0.1) {
      setSfxVolume(0.3);
      updateStorage('sfxVolume', 0.3);
    }
  }, [sfxVolume]);

  useEffect(() => {
    localStorage.setItem('spatialAudioEnabled', JSON.stringify(false));
    setSpatialAudioEnabled(false);
  }, []);

  const handleSetNotifications = (checked: boolean) => {
    setNotifications(checked);
    updateStorage('notifications', checked);
  };

  const handleSetSpeakingNotifications = (checked: boolean) => {
    setSpeakingNotifications(checked);
    updateStorage('speakingNotifications', checked);
  };

  const handleSetCaptureSystemAudio = (checked: boolean) => {
    setCaptureSystemAudio(checked);
    updateStorage('captureSystemAudio', checked);
  };

  const handleSetAutoConnectVoice = (checked: boolean) => {
    setAutoConnectVoice(checked);
    updateStorage('autoConnectVoice', checked);
  };

  const toggleAutoConnectVoice = () => {
    setAutoConnectVoice((prev) => {
      const newValue = !prev;
      updateStorage('autoConnectVoice', newValue);
      return newValue;
    });
  };

  const handleSetAudioOutputDeviceId = (deviceId: string) => {
    setSelectedAudioOutputDeviceId(deviceId);
    updateStorage('audioOutputDeviceId', deviceId, false);
  };

  const handleSetSFXVolume = (volume: number) => {
    const clamped = Math.min(1.0, Math.max(0.0, volume));
    setSfxVolume(clamped);
    updateStorage('sfxVolume', clamped);
  };

  const handleSetAutoGainControl = (enabled: boolean) => {
    setAutoGainControl(enabled);
    updateStorage('autoGainControl', enabled);
  };

  const handleSetNoiseSuppression = (enabled: boolean) => {
    setNoiseSuppression(enabled);
    updateStorage('CENC', enabled);
  };

  const handleSetEchoCancellation = (enabled: boolean) => {
    setEchoCancellation(enabled);
    updateStorage('echoCancellation', enabled);
  };

  const toggleServerMute = (serverId: string) => {
    setMutedServers((prev) => {
      const next = {
        ...prev,
        [serverId]: !prev[serverId],
      };
      updateStorage('mutedServers', next);
      return next;
    });
  };

  const handleSetSpatialAudioEnabled = (enabled: boolean) => {
    setSpatialAudioEnabled(enabled);
    updateStorage('spatialAudioEnabled', enabled);
  };

  const handleSetUserStartPlayingNotifications = (checked: boolean) => {
    setUserStartPlayingNotifications(checked);
    updateStorage('userStartPlayingNotifications', checked);
  };

  const handleSetAudioInputDeviceId = (deviceId: string) => {
    setSelectedAudioInputDeviceId(deviceId);
    updateStorage('audioInputDeviceId', deviceId, false);
    Cookies.set('audioInputDeviceId', deviceId, { path: '/' });
  };

  const handleSetReceiveScreenShare = (checked: boolean) => {
    setReceiveScreenShare(checked);
    updateStorage('receiveScreenShare', checked);
  };

  return (
    <SettingsContext.Provider
      value={{
        notifications,
        setNotifications: handleSetNotifications,
        speakingNotifications,
        setSpeakingNotifications: handleSetSpeakingNotifications,
        userStartPlayingNotifications,
        setUserStartPlayingNotifications: handleSetUserStartPlayingNotifications,
        captureSystemAudio,
        setCaptureSystemAudio: handleSetCaptureSystemAudio,
        autoConnectVoice,
        setAutoConnectVoice: handleSetAutoConnectVoice,
        toggleAutoConnectVoice,
        audioOutputDevices: audioOutputDevices,
        setAudioOutputDevices,
        selectedAudioOutputDeviceId,
        setSelectedAudioOutputDeviceId: handleSetAudioOutputDeviceId,
        audioOutputError,
        setAudioOutputError,
        sfxVolume,
        setSFXVolume: handleSetSFXVolume,
        autoGainControl,
        setAutoGainControl: handleSetAutoGainControl,
        noiseSuppression,
        setNoiseSuppression: handleSetNoiseSuppression,
        echoCancellation,
        setEchoCancellation: handleSetEchoCancellation,
        mutedServers,
        toggleServerMute,
        spatialAudioEnabled,
        setSpatialAudioEnabled: handleSetSpatialAudioEnabled,
        selectedAudioInputDeviceId,
        setSelectedAudioInputDeviceId: handleSetAudioInputDeviceId,
        receiveScreenShare,
        setReceiveScreenShare: handleSetReceiveScreenShare,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
